import { Component, type ErrorInfo, type ReactNode } from "react"
import { captureError } from "./sentry"

interface Props {
    children: ReactNode
}

interface State {
    hasError: boolean
}

/**
 * Top-level safety net. A render-time throw anywhere in the tree would
 * otherwise blank the screen with no recovery; instead we show a minimal
 * "something went wrong" panel with a reload. Kept deliberately dependency-free
 * (no theme/animation imports) so the fallback can't itself fail to render.
 */
export class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false }

    static getDerivedStateFromError(): State {
        return { hasError: true }
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error("Unhandled render error:", error, info.componentStack)
        captureError(error, { componentStack: info.componentStack })
    }

    render() {
        if (!this.state.hasError) return this.props.children

        return (
            <div
                role="alert"
                style={{
                    minHeight: "100dvh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 16,
                    padding: 24,
                    textAlign: "center",
                    background: "#f4ecd8",
                    color: "#1d1a14",
                    fontFamily: "system-ui, sans-serif",
                }}
            >
                <h1 style={{ margin: 0, fontSize: 22 }}>Something went wrong</h1>
                <p style={{ margin: 0, maxWidth: 320, opacity: 0.8 }}>
                    The app hit an unexpected error. Reloading usually fixes it.
                </p>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    style={{
                        marginTop: 8,
                        padding: "12px 24px",
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#f4ecd8",
                        background: "#1d1a14",
                        border: "none",
                        borderRadius: 8,
                        cursor: "pointer",
                    }}
                >
                    Reload
                </button>
            </div>
        )
    }
}
